import React from "react";
import { Button } from "components/ui/Button";
import { ConnectionState } from "apps/user/learner/hooks/use-ori-connection";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "../styles.module.scss";

type BannerState = "reconnecting" | "error";

interface OriConnectionBannerProps {
  connectionState: ConnectionState;
  reconnect: () => void;
}

const OriConnectionBanner: React.FC<OriConnectionBannerProps> = ({
  connectionState,
  reconnect,
}) => {
  const { t } = useTranslation(["ori", "common"]);
  // Routine connecting/connected states are conveyed by the composer; only
  // surface the banner when something needs attention.
  if (connectionState !== "reconnecting" && connectionState !== "error") {
    return null;
  }

  const statusLabel: Record<BannerState, string> = {
    reconnecting: t("ori:chat.status.reconnecting"),
    error: t("ori:chat.connectionBanner.errorLabel"),
  };
  const statusMessage: Record<BannerState, string> = {
    reconnecting: t("ori:chat.connectionBanner.reconnectingMessage"),
    error: t("ori:chat.connectionBanner.errorMessage"),
  };

  return (
    <div className={styles.bannerRow}>
      <span
        className={`${styles.statusBanner} ${styles[connectionState]}`}
        data-testid="ori-connection-status"
        data-state={connectionState}
      >
        <span className={styles.statusDot} aria-hidden />
        {statusLabel[connectionState]}
      </span>
      <p className={styles.statusMessage}>{statusMessage[connectionState]}</p>
      {connectionState === "error" && (
        <Button
          variant="enclosed-primary"
          size="small"
          onClick={reconnect}
          data-testid="ori-retry"
        >
          {t("common:action.retry")}
        </Button>
      )}
    </div>
  );
};

export default OriConnectionBanner;
